<script setup lang="ts">
import { fetchMaterialIconOptions } from "@/service/IconServices";
import { ChevronDownIcon, ChevronUpIcon } from "@gohighlevel/ghl-icons/24/outline";
import startCase from "lodash/startCase";
import { computed, onMounted, ref } from "vue";

interface Props {
  modelValue: string;
  label: string;
}
const props = defineProps<Props>();
const emit = defineEmits(["update:modelValue"]);

const isOpen = ref(false);
const search = ref("");
const options = ref<Array<{ label: string; value: string }>>([]);

onMounted(async () => {
  options.value = await fetchMaterialIconOptions();
});

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) {
    return options.value;
  }
  return options.value.filter((o) => o.label.toLowerCase().includes(q));
});

const selectIcon = (name: string) => {
  emit("update:modelValue", name);
  isOpen.value = false;
};

const displayLabel = computed(() => {
  return startCase(props.modelValue || "");
});
</script>

<template>
  <div>
    <div class="hl-text-xs-medium mb-2">{{ label }}</div>
    <div class="relative">
      <div
        class="flex w-full cursor-pointer items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500"
        aria-haspopup="listbox"
        :aria-expanded="isOpen"
        @click="isOpen = !isOpen"
      >
        <div class="flex items-center gap-2 truncate">
          <span
            class="material-symbols-outlined inline-flex h-5 w-5 items-center justify-center text-gray-700"
            style="font-size: 20px"
            aria-hidden="true"
          >
            {{ props.modelValue }}
          </span>
          <span class="truncate text-gray-900">{{ displayLabel }}</span>
        </div>
        <div class="flex items-center gap-3">
          <ChevronDownIcon v-if="!isOpen" class="h-5 w-5 text-gray-500" />
          <ChevronUpIcon v-else class="h-5 w-5 text-gray-500" />
        </div>
      </div>

      <div
        v-if="isOpen"
        class="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white p-2 shadow-lg"
      >
        <input
          v-model="search"
          type="text"
          class="mb-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          :placeholder="$t('common.search')"
          aria-label="Icon search"
        />
        <div v-if="filtered.length" class="max-h-64 overflow-y-auto">
          <div class="grid grid-cols-6 gap-2">
            <button
              v-for="opt in filtered"
              :key="opt.value"
              type="button"
              class="icon-item flex items-center justify-center rounded-md border border-gray-200 p-2 hover:bg-gray-50"
              :class="{ 'icon-item--selected': opt.value === props.modelValue }"
              :aria-label="opt.label"
              :aria-pressed="opt.value === props.modelValue"
              @click="selectIcon(opt.value)"
            >
              <span class="material-symbols-outlined icon-glyph text-gray-800" aria-hidden="true">
                {{ opt.value }}
              </span>
            </button>
          </div>
        </div>
        <div v-else class="py-6 text-center text-sm text-gray-500">
          {{ $t("common.noResults") }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.icon-item .icon-glyph {
  font-size: 20px;
  transition: transform 120ms ease, color 120ms ease;
}
.icon-item:hover .icon-glyph {
  transform: scale(1.2);
  color: #155eef;
}
.icon-item--selected {
  border-color: #155eef;
  background-color: #f5f8ff;
}
.icon-item--selected .icon-glyph {
  color: #155eef;
  transform: scale(1.05);
}
</style>
